import { Nullable } from 'common-types';
import { snapshot } from 'valtio';
import {
  getAppsNamesFromIDs,
  getSelectedAppsIDs,
} from '../../applications/utils/application-utils';
import {
  closeSnackbar,
  openSnackbar,
} from '../../snackbar/actions/snackbar-actions';
import {
  BirthCertificateResponseType,
  FetchReadyPBOMInput,
  GeneratePBOMInput,
} from '../birth-certificate-types';
import birthCertificateService from '../services';
import { FetchPBOMResponse } from '../services/fetch-ready-pbom/fetch-ready-pbom';
import downloadPBOMStore from '../stores/pbom-store';
import { PDFServicePoller } from '../utils/pdf-service-poller';
import { downloadFileFromURL } from '../utils/save-file-to-disc-utils';

const downloadSnackBarKey = 'download-pbom-snackbar';

const pollPBOM = (input: FetchReadyPBOMInput) => {
  const response = birthCertificateService.fetchReadyPBOM.execute(input);

  return response;
};

const handlePBOMCallback = (
  response: Nullable<FetchPBOMResponse['fetchReadyFile']>,
  appName: string,
) => {
  if (response) {
    const { totalReturnedCount, downloadInProgress, totalErrored } =
      snapshot(downloadPBOMStore);
    if (response.error) {
      openSnackbar(`Download failed for ${appName}`, { variant: 'error' });
      downloadPBOMStore.totalErrored = totalErrored + 1;
      return false;
    }
    if (response.isFileReady) {
      if (downloadInProgress) {
        return true;
      }
      downloadFileFromURL(response.downloadLink!);
      downloadPBOMStore.totalReturnedCount = totalReturnedCount + 1;
      downloadPBOMStore.downloadInProgress = true;
      const ref = document.getElementById(downloadSnackBarKey);
      if (ref) {
        ref.innerText = `Generating ${
          downloadPBOMStore.totalSentCount === 1
            ? 'PBOM'
            : `${downloadPBOMStore.totalReturnedCount} / ${downloadPBOMStore.totalSentCount} PBOMs`
        } `;
      }
      setTimeout(() => {
        downloadPBOMStore.downloadInProgress = false;
      }, 1000);
    }
    return !response.isFileReady;
  } else {
    return true;
  }
};

const pbomDoneCallback = () => {
  const { totalReturnedCount, totalSentCount, totalErrored } =
    snapshot(downloadPBOMStore);
  if (totalReturnedCount + totalErrored === totalSentCount) {
    closeSnackbar(downloadSnackBarKey);
    openSnackbar(
      `Successfully downloaded ${totalReturnedCount} / ${totalSentCount} ${
        totalReturnedCount === 1 ? 'PBOM' : `PBOMs`
      } `,
      {
        variant: 'success',
      },
    );
    downloadPBOMStore.totalReturnedCount = 0;
    downloadPBOMStore.totalSentCount = 0;
    downloadPBOMStore.totalErrored = 0;
  }
};

export const generateBirthCertificate = async (
  appId: string,
  responseType: BirthCertificateResponseType,
  appName: string,
): Promise<boolean> => {
  const snackbarKey = `generate-birth-certificate-${appId}`;
  const input: GeneratePBOMInput = {
    appId,
    responseType,
  };

  const response =
    await birthCertificateService.generateBirthCertificate.execute(input);

  closeSnackbar(snackbarKey);

  if (!response) {
    closeSnackbar(downloadSnackBarKey);
    openSnackbar(`Download failed for ${appName}`, { variant: 'error' });
    return false;
  }
  const poller = new PDFServicePoller<FetchPBOMResponse['fetchReadyFile']>(
    () => {
      return pollPBOM({ requestId: response.requestId });
    },
    (response: Nullable<FetchPBOMResponse['fetchReadyFile']>) => {
      return handlePBOMCallback(response, appName);
    },
    // TODO: add error handling below
    () => {},
    pbomDoneCallback,
  );

  poller.startPolling();
  setTimeout(() => {
    poller.cancelPolling();
  }, 360000);

  return true;
};

export const downloadBirthCertificate = async (
  responseType: BirthCertificateResponseType,
) => {
  const appIds = getSelectedAppsIDs();
  const appNames = getAppsNamesFromIDs(appIds);
  const promises: Promise<boolean>[] = [];
  downloadPBOMStore.totalSentCount = appIds.length;
  // Cannot use pluralize lib here until this issue is fixed (we want to have PBOMs and not PBOMS):
  // https://github.com/plurals/pluralize/issues/131
  openSnackbar(
    `Generating ${
      appIds.length === 1
        ? 'PBOM'
        : `${downloadPBOMStore.totalReturnedCount} / ${appIds.length} PBOMs`
    } `,
    {
      variant: 'default',
      persist: true,
      key: downloadSnackBarKey,
    },

    false, // hideCloseIcon
    true, // isShowingSpinner
  );
  appIds.forEach(appId => {
    promises.push(
      generateBirthCertificate(appId, responseType, appNames[appId]),
    );
  });
  await Promise.all(promises);
};
